import { useMemo, useState } from "react";
import { MdAdd, MdBolt, MdCloudQueue } from "react-icons/md";

import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Table,
  Text,
  VStack,
} from "components/ui";
import DeleteConfirmPopover from "components/common/DeleteConfirmPopover";
import { useTranslation } from "hooks/useTranslation";
import {
  AiConnector,
  AiConnectorHealth,
  AiConnectorTestResult,
  aiGatewayService,
} from "services/aiGatewayService";
import { getThemeColor } from "theme/colors";

import ConnectorModal from "./ConnectorModal";

interface ConnectorsPanelProps {
  connectors: AiConnector[];
  /** Only failing connectors appear — a connector with no row here is healthy. */
  health: AiConnectorHealth[];
  loading: boolean;
  /** Reloads every panel — a connector change moves the model list and the routing table too. */
  onChanged: () => void;
}

/**
 * Four states, from three fields. A connector with no health row has never failed; a `Closed` row
 * with failures on it has failed recently without tripping, which is worth showing as degraded
 * rather than flattening into "healthy" — it is usually the first sign of a key about to expire.
 */
const healthBadge = (row?: AiConnectorHealth) => {
  if (!row || (row.state === "Closed" && row.consecutiveFailures === 0)) {
    return { color: "green", key: "healthy" };
  }
  if (row.state === "Open") return { color: "red", key: "open" };
  if (row.state === "HalfOpen") return { color: "orange", key: "recovering" };
  return { color: "yellow", key: "degraded" };
};

/**
 * Connector list with create / edit / delete and a one-token test probe.
 *
 * A connector with `companyId === null` belongs to the platform: it is visible here because the
 * company can resolve to it, but editing it would change every other tenant's configuration, so
 * its row actions are replaced with a badge.
 *
 * The Health column is the circuit breaker's view rather than a live check — an open circuit means
 * the gateway is skipping this connector without calling it, which is why the row also carries the
 * provider's own reason. Use Test for a live answer.
 */
const ConnectorsPanel = ({
  connectors,
  health,
  loading,
  onChanged,
}: ConnectorsPanelProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<AiConnector | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<
    Record<string, AiConnectorTestResult>
  >({});

  const healthByConnector = useMemo(
    () =>
      health.reduce<Record<string, AiConnectorHealth>>((map, row) => {
        map[row.aiConnectorId] = row;
        return map;
      }, {}),
    [health],
  );

  const openCreate = () => {
    setEditing(null);
    setIsModalOpen(true);
  };

  const openEdit = (connector: AiConnector) => {
    setEditing(connector);
    setIsModalOpen(true);
  };

  const handleTest = async (connector: AiConnector) => {
    setTestingId(connector.id);
    try {
      const result = await aiGatewayService.testConnector(connector.id);
      setTestResults((prev) => ({ ...prev, [connector.id]: result }));
    } catch (err: any) {
      // `testConnector` is `__silent`: a transport failure is still "the test did not pass",
      // and the row renders that below rather than firing a toast the user must dismiss.
      setTestResults((prev) => ({
        ...prev,
        [connector.id]: {
          success: false,
          connectorName: connector.name,
          provider: connector.provider,
          model: null,
          apiDialect: connector.apiDialect,
          durationMs: 0,
          errorCode: null,
          errorMessage:
            err?.response?.status === 429
              ? t("app.aiGateway.connectors.testRateLimited")
              : t("app.aiGateway.connectors.testUnreachable"),
        },
      }));
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (connector: AiConnector) => {
    setDeletingId(connector.id);
    try {
      await aiGatewayService.deleteConnector(connector.id);
      onChanged();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <VStack align="stretch" gap={4}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Text fontSize="sm" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.connectors.subtitle")}
        </Text>
        <Button intent="primary" size="sm" onClick={openCreate}>
          <MdAdd />
          {t("app.aiGateway.connectors.add")}
        </Button>
      </Flex>

      {!loading && connectors.length === 0 && (
        <VStack gap={3} py={10}>
          <Box color={getThemeColor("text.secondary")} fontSize="3xl">
            <MdCloudQueue />
          </Box>
          <Text color={getThemeColor("text.secondary")} textAlign="center">
            {t("app.aiGateway.connectors.empty")}
          </Text>
          <Button intent="primary" size="sm" onClick={openCreate}>
            <MdAdd />
            {t("app.aiGateway.connectors.add")}
          </Button>
        </VStack>
      )}

      {connectors.length > 0 && (
        <Box overflowX="auto">
          <Table.Root size="sm" variant="outline" minW="1040px">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  {t("app.aiGateway.table.name")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.table.kind")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.table.dialect")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.table.endpoint")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.table.key")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.table.health")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.table.status")}
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.table.actions")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {connectors.map((connector) => {
                const isPlatform = connector.companyId === null;
                const result = testResults[connector.id];
                const healthRow = healthByConnector[connector.id];
                const badge = healthBadge(healthRow);
                return (
                  <Table.Row key={connector.id}>
                    <Table.Cell>
                      <VStack align="start" gap={1}>
                        <HStack gap={2}>
                          <Text fontWeight="medium">{connector.name}</Text>
                          {connector.isDefault && (
                            <Badge colorPalette="blue" size="sm">
                              {t("app.aiGateway.table.default")}
                            </Badge>
                          )}
                          {isPlatform && (
                            <Badge colorPalette="gray" size="sm">
                              {t("app.aiGateway.table.managedByPlatform")}
                            </Badge>
                          )}
                          {connector.bypassEgressPolicy && (
                            <Badge colorPalette="orange" size="sm">
                              {t("app.aiGateway.table.egressBypass")}
                            </Badge>
                          )}
                        </HStack>
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                        >
                          {connector.provider}
                        </Text>
                        {result && (
                          <Text
                            fontSize="xs"
                            color={result.success ? "green.500" : "red.500"}
                          >
                            {result.success
                              ? t("app.aiGateway.connectors.testPassed", {
                                  model: result.model ?? "",
                                  ms: result.durationMs,
                                })
                              : t("app.aiGateway.connectors.testFailed", {
                                  reason:
                                    result.errorMessage ??
                                    t("app.aiGateway.connectors.testUnknown"),
                                })}
                          </Text>
                        )}
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>
                      {t(`app.aiGateway.kinds.${connector.kind}`)}
                    </Table.Cell>
                    <Table.Cell>
                      {t(`app.aiGateway.dialects.${connector.apiDialect}`)}
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        fontSize="xs"
                        color={getThemeColor("text.secondary")}
                        maxW="240px"
                        truncate
                      >
                        {connector.baseUrl ??
                          t("app.aiGateway.table.providerDefaultUrl")}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      {connector.hasApiKey ? (
                        <Badge
                          colorPalette={
                            connector.isApiKeyEncrypted ? "green" : "orange"
                          }
                          size="sm"
                        >
                          {connector.isApiKeyEncrypted
                            ? t("app.aiGateway.table.keyEncrypted")
                            : t("app.aiGateway.table.keyPlaintext")}
                        </Badge>
                      ) : (
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                        >
                          {t("app.aiGateway.table.keyNone")}
                        </Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <VStack align="start" gap={1}>
                        <Badge colorPalette={badge.color} size="sm">
                          {t(`app.aiGateway.health.${badge.key}`)}
                        </Badge>
                        {healthRow && badge.key !== "healthy" && (
                          <Text
                            fontSize="xs"
                            color={getThemeColor("text.secondary")}
                            maxW="220px"
                            truncate
                          >
                            {healthRow.lastFailureReason ??
                              t("app.aiGateway.health.noReason")}
                          </Text>
                        )}
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        colorPalette={connector.isActive ? "green" : "gray"}
                        size="sm"
                      >
                        {connector.isActive
                          ? t("app.common.active")
                          : t("app.common.inactive")}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={2} justify="flex-end">
                        <Button
                          intent="ghost"
                          size="sm"
                          loading={testingId === connector.id}
                          onClick={() => handleTest(connector)}
                        >
                          <MdBolt />
                          {t("app.aiGateway.connectors.test")}
                        </Button>
                        {!isPlatform && (
                          <>
                            <Button
                              intent="ghost"
                              size="sm"
                              onClick={() => openEdit(connector)}
                            >
                              {t("app.common.edit")}
                            </Button>
                            <DeleteConfirmPopover
                              loading={deletingId === connector.id}
                              onConfirm={() => handleDelete(connector)}
                              title={t("app.aiGateway.connectors.deleteTitle")}
                              message={t(
                                "app.aiGateway.connectors.deleteMessage",
                              )}
                              triggerLabel={t("app.common.delete")}
                            />
                          </>
                        )}
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        </Box>
      )}

      <ConnectorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        connector={editing}
        onSaved={onChanged}
      />
    </VStack>
  );
};

export default ConnectorsPanel;
